import React, { useCallback } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap, cx } from '@atlaskit/css';
import AlignImageCenterIcon from '@atlaskit/icon/core/align-image-center';
import AlignImageLeftIcon from '@atlaskit/icon/core/align-image-left';
import AlignImageRightIcon from '@atlaskit/icon/core/align-image-right';
import { Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { useIsDisabledContext } from '../../../../controllers/disabled-context';
import { Alignment, type RenderVariant } from '../../../../types';
import { type UpdateMainFormFieldType } from '../../../items-shared/types';
import { messages } from './messages';

const styles = cssMap({
	borderOptionContainer: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.050'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	border: {
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
	},
	selected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		borderColor: token('color.border.accent.blue'),
	},
});

const alignmentOptions = [
	{ label: messages.leftAlignmentLabel, IconComponent: AlignImageLeftIcon, value: Alignment.LEFT },
	{
		label: messages.centerAlignmentLabel,
		IconComponent: AlignImageCenterIcon,
		value: Alignment.CENTER,
	},
	{
		label: messages.rightAlignmentLabel,
		IconComponent: AlignImageRightIcon,
		value: Alignment.RIGHT,
	},
];

interface AlignmentFieldForm {
	alignment?: Alignment;
}
interface AlignmentFieldProps {
	renderVariant?: RenderVariant;
	selectedAlignment?: Alignment;
	source: string;
	updateMainFormField: UpdateMainFormFieldType<AlignmentFieldForm>;
}

export const AlignmentField = ({
	selectedAlignment,
	updateMainFormField,
	renderVariant,
	source,
}: AlignmentFieldProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isDisabled = useIsDisabledContext();

	const handleSelect = useCallback(
		(newValue: Alignment) => {
			updateMainFormField({ alignment: newValue });

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'buttonGroup',
					actionSubjectId: 'alignment',
					source: source,
					attributes: {
						renderVariant,
						selectedValue: newValue,
					},
				},
			}).fire();
		},
		[updateMainFormField, createAnalyticsEvent, renderVariant, source],
	);

	return (
		<Inline alignBlock="center" spread="space-between">
			<Text color="color.text">
				<FormattedMessage {...messages.alignmentLabel} />
			</Text>
			<Inline space="space.075">
				{alignmentOptions.map(({ label, IconComponent, value }) => (
					<Tooltip content={<FormattedMessage {...label} />} hideTooltipOnMouseDown key={value}>
						<Pressable
							xcss={cx(
								styles.borderOptionContainer,
								styles.border,
								value === selectedAlignment && styles.selected,
							)}
							onClick={() => handleSelect(value)}
							isDisabled={isDisabled}
						>
							<IconComponent
								color={
									value === selectedAlignment
										? token('color.icon.accent.blue')
										: token('color.icon')
								}
								label={formatMessage(label)}
							/>
						</Pressable>
					</Tooltip>
				))}
			</Inline>
		</Inline>
	);
};
